import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Chip
} from '@mui/material';
import { CheckCircle, Cancel, Schedule, Warning } from '@mui/icons-material';
import { externalAPI } from '../services/api';
import { format } from 'date-fns';

interface Notice {
  id: string;
  notice_number: string;
  status: string;
  min_sell_price: number;
  min_price_allowed?: number;
  share_quantity_total: number;
  start_date: string;
  end_date: string;
  activated_at?: string;
  notes?: string;
  share_sales?: Array<{ shares_sold: number; total_value: number }>;
}

interface ExternalData {
  notice: Notice;
  tokenType: string;
  allowedActions: string[];
}

const ExternalNotice: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [data, setData] = useState<ExternalData | null>(null);
  
  // Dialog states
  const [activateOpen, setActivateOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [completeData, setCompleteData] = useState({ shares_sold: '', average_sell_price: '', trade_notes: '' });
  const [actionLoading, setActionLoading] = useState(false);

  const loadNotice = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      setError('');
      const response = await externalAPI.getNotice(token);
      setData(response);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Invalid or expired link. Please contact Locate Technologies for a new link.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      loadNotice();
    }
  }, [token, loadNotice]);

  const handleActivate = async () => {
    if (!token) return;
    setActionLoading(true);
    try {
      await externalAPI.activate(token);
      setSuccess('Notice activated successfully! You can now sell shares under this authorization.');
      setActivateOpen(false);
      loadNotice();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to activate notice');
    } finally {
      setActionLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!token) return;
    setActionLoading(true);
    try {
      await externalAPI.complete(token, {
        shares_sold: parseInt(completeData.shares_sold),
        average_sell_price: completeData.average_sell_price ? parseFloat(completeData.average_sell_price) : undefined,
        trade_notes: completeData.trade_notes || undefined
      });
      setSuccess('Notice completed successfully! Thank you for recording the trade.');
      setCompleteOpen(false);
      setCompleteData({ shares_sold: '', average_sell_price: '', trade_notes: '' });
      loadNotice();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to complete notice');
    } finally {
      setActionLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <CheckCircle color="success" />;
      case 'pending': return <Schedule color="warning" />;
      case 'completed': return <CheckCircle color="info" />;
      case 'cancelled': return <Cancel color="error" />;
      case 'expired': return <Warning color="error" />;
      default: return <Schedule />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'info' | 'error' | 'default' => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'completed': return 'info';
      case 'cancelled': return 'default';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        bgcolor: '#f5f5f5'
      }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !data) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        bgcolor: '#f5f5f5'
      }}>
        <Container maxWidth="sm">
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <Warning sx={{ fontSize: 64, color: 'error.main', mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Link Invalid or Expired
              </Typography>
              <Typography color="text.secondary" paragraph>
                {error}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                If you need assistance, please contact Locate Technologies.
              </Typography>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  const notice = data?.notice;
  const canActivate = data?.allowedActions.includes('activate');
  const canComplete = data?.allowedActions.includes('complete');
  const totalSharesSold = notice?.share_sales?.reduce((sum, sale) => sum + sale.shares_sold, 0) || 0;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 4 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Paper sx={{ p: 3, mb: 3, textAlign: 'center' }}>
          <Typography variant="overline" color="primary">
            Novus Capital Portal
          </Typography>
          <Typography variant="h4" gutterBottom>
            ATM Notice Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Secure access provided by Locate Technologies
          </Typography>
        </Paper>

        {/* Alerts */}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {notice && (
          <>
            {/* Notice Card */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {getStatusIcon(notice.status)}
                    <Typography variant="h5">
                      {notice.notice_number}
                    </Typography>
                  </Box>
                  <Chip 
                    label={notice.status.toUpperCase()} 
                    color={getStatusColor(notice.status)}
                    size="medium"
                  />
                </Box>
                
                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Minimum Sell Price</Typography>
                    <Typography variant="h6">{formatCurrency(notice.min_sell_price)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Shares Authorized</Typography>
                    <Typography variant="h6">{notice.share_quantity_total.toLocaleString()}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">Active Period</Typography>
                    <Typography variant="body1">
                      {format(new Date(notice.start_date), 'MMM d, yyyy')} — {format(new Date(notice.end_date), 'MMM d, yyyy')}
                    </Typography>
                  </Box>
                  {notice.activated_at && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">Activated On</Typography>
                      <Typography variant="body1">
                        {format(new Date(notice.activated_at), 'MMM d, yyyy HH:mm')}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {notice.notes && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">Notes</Typography>
                    <Typography variant="body1">{notice.notes}</Typography>
                  </Box>
                )}

                {totalSharesSold > 0 && (
                  <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">Shares Sold So Far</Typography>
                    <Typography variant="h6">{totalSharesSold.toLocaleString()}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Remaining: {(notice.share_quantity_total - totalSharesSold).toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Action Buttons */}
            {(canActivate || canComplete) && (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>Available Actions</Typography>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {canActivate && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Activate this notice to begin selling shares under this authorization.
                        </Typography>
                        <Button 
                          variant="contained" 
                          color="success" 
                          size="large"
                          onClick={() => setActivateOpen(true)}
                          fullWidth
                        >
                          Activate Notice
                        </Button>
                      </Box>
                    )}
                    
                    {canComplete && (
                      <Box>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Record the shares sold to complete this notice.
                        </Typography>
                        <Button 
                          variant="contained" 
                          color="primary" 
                          size="large"
                          onClick={() => setCompleteOpen(true)}
                          fullWidth
                        >
                          Complete Notice
                        </Button>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}

            {!canActivate && !canComplete && notice.status !== 'pending' && notice.status !== 'active' && (
              <Card>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    This notice is {notice.status}. No further actions are available.
                  </Typography>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {/* Activate Dialog */}
        <Dialog open={activateOpen} onClose={() => setActivateOpen(false)}>
          <DialogTitle>Activate Notice</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to activate notice <strong>{notice?.notice_number}</strong>?
            </DialogContentText>
            <DialogContentText sx={{ mt: 2 }}>
              This will authorize the sale of up to <strong>{notice?.share_quantity_total.toLocaleString()}</strong> shares 
              at a minimum price of <strong>{notice && formatCurrency(notice.min_sell_price)}</strong>.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActivateOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button onClick={handleActivate} variant="contained" color="success" disabled={actionLoading}>
              {actionLoading ? 'Activating...' : 'Confirm Activation'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Complete Dialog */}
        <Dialog open={completeOpen} onClose={() => setCompleteOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Complete Notice</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Enter the share sale details to complete notice <strong>{notice?.notice_number}</strong>.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="Total Shares Sold"
              type="number"
              fullWidth
              required
              value={completeData.shares_sold}
              onChange={(e) => setCompleteData(prev => ({ ...prev, shares_sold: e.target.value }))}
              inputProps={{ 
                min: 1, 
                max: notice ? notice.share_quantity_total - totalSharesSold : undefined 
              }}
              helperText={notice ? `Maximum: ${(notice.share_quantity_total - totalSharesSold).toLocaleString()}` : ''}
              sx={{ mt: 2 }}
            />
            <TextField
              margin="dense"
              label="Average Sell Price (Optional)"
              type="number"
              fullWidth
              value={completeData.average_sell_price}
              onChange={(e) => setCompleteData(prev => ({ ...prev, average_sell_price: e.target.value }))}
              inputProps={{ 
                min: notice?.min_sell_price, 
                step: '0.0001' 
              }}
              helperText={notice ? `Minimum: ${formatCurrency(notice.min_sell_price)}` : ''}
            />
            <TextField
              margin="dense"
              label="Trade Notes (Optional)"
              fullWidth
              multiline
              rows={2}
              value={completeData.trade_notes}
              onChange={(e) => setCompleteData(prev => ({ ...prev, trade_notes: e.target.value }))}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCompleteOpen(false)} disabled={actionLoading}>
              Cancel
            </Button>
            <Button 
              onClick={handleComplete} 
              variant="contained" 
              disabled={actionLoading || !completeData.shares_sold}
            >
              {actionLoading ? 'Completing...' : 'Mark as Complete'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Footer */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            ATM Facility Management Platform
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Powered by Locate Technologies
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ExternalNotice;

